import './MonthRangePicker.css'
import cx from 'classnames'
import { useMemo, useState, useCallback, memo } from 'react'
import { lastDayOfMonth } from 'date-fns'
import { Tooltip } from '../Tooltip/Tooltip'
import { buttonClasses } from '../Button/Button'
export interface DisabledMonth {
  date: Date
  reason: 'reported' | 'future' | 'outOfSpan'
}

export interface MonthRangePickerProps {
  /** Months that cannot be selected */
  disabledMonths?: DisabledMonth[]
  /** Called whenever selection changes */
  onChange?: (range: { start: Date | null; end: Date | null }) => void
  /** Additional className for container */
  className?: string
  /** Months to display in the calendar (12 consecutive months). If `label` is omitted, it defaults to the browser-locale short month name via Intl. */
  months?: { date: Date; label?: string }[]
  /** Optional tooltip messages for disabled months. If omitted or empty, no tooltip will be shown for disabled months. */
  tooltips?: Partial<Record<DisabledMonth['reason'], string>>
  /** Optional locale – defaults to browser locale (navigator.language) */
  locale?: string
  /** Optional placeholder header */
  placeholderHeader?: string
}

function isSameMonth(firstDate: Date, secondDate: Date) {
  return (
    firstDate.getFullYear() === secondDate.getFullYear() &&
    firstDate.getMonth() === secondDate.getMonth()
  )
}

function getMonthId(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

// Represent a month entry in the calendar grid
interface CalendarMonth {
  date: Date
  label: string
}

interface MonthButtonProps {
  month: CalendarMonth
  isDisabled: boolean
  isSelected: boolean
  isStartDate: boolean
  isEndDate: boolean
  isInRange: boolean
  tooltipMessage?: string
  onSelect: () => void
}

const selectedVariantClass = cx(
  'month-button-base',
  buttonClasses.variantToClasses['default'],
  'text-main'
)

const inRangeVariantClass = cx('month-button-base', buttonClasses.variantToClasses['subtle'])
const disabledVariantClass = 'month-button-base month-button-disabled'

const MonthButton = memo(function MonthButton({
  month,
  isDisabled,
  isSelected,
  isStartDate,
  isEndDate,
  isInRange,
  tooltipMessage,
  onSelect
}: MonthButtonProps) {
  // Determine styling based on state

  const showDefaultVariant = !isDisabled && !isInRange && !isSelected && !isStartDate && !isEndDate
  const showSelectedVariant = (isSelected || isStartDate || isEndDate) && !isDisabled
  const showSubtleVariant = isInRange && !isStartDate && !isEndDate && !isDisabled
  const classNames = cx({
    'month-button-base': showDefaultVariant,
    [selectedVariantClass]: showSelectedVariant,
    [inRangeVariantClass]: showSubtleVariant,
    [disabledVariantClass]: isDisabled,
    'month-button-start': isStartDate,
    'month-button-end': isEndDate
  })

  const buttonElement = (
    <button
      key={month.date.toISOString()}
      type="button"
      disabled={isDisabled}
      className={classNames}
      onClick={onSelect}
      aria-pressed={isSelected}
    >
      {month.label}
    </button>
  )

  if (isDisabled && tooltipMessage) {
    return (
      <Tooltip key={month.date.toISOString()} content={tooltipMessage} position="top">
        {buttonElement}
      </Tooltip>
    )
  }

  return buttonElement
})

export function MonthRangePicker({
  disabledMonths = [],
  months,
  onChange,
  tooltips,
  className,
  locale,
  placeholderHeader
}: MonthRangePickerProps) {
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const monthFormatter = useMemo(() => {
    const effectiveLocale =
      locale ?? (typeof navigator !== 'undefined' ? navigator.language : 'en-US')
    return new Intl.DateTimeFormat(effectiveLocale, { month: 'short' })
  }, [locale])

  // Build map for quick disabled lookup
  const disabledMap = useMemo(() => {
    const map = new Map<string, DisabledMonth['reason']>()
    disabledMonths.forEach(disabledMonth => {
      const key = getMonthId(disabledMonth.date)
      map.set(key, disabledMonth.reason)
    })
    return map
  }, [disabledMonths])

  // Determine months to render – either provided via props or last 12 months up to current month
  const monthsToRender = useMemo(() => {
    if (months && months.length > 0) {
      // Ensure each entry has a label, defaulting to intl formatter
      return months.map(month => ({
        ...month,
        label: month.label ?? monthFormatter.format(month.date)
      }))
    }

    // Fallback: generate last 12 months ending with the current month
    const now = new Date()
    const generatedMonths: CalendarMonth[] = []
    for (let i = 11; i >= 0; i--) {
      const generatedDate = new Date(now.getFullYear(), now.getMonth() - i, 1)
      generatedMonths.push({ date: generatedDate, label: monthFormatter.format(generatedDate) })
    }
    return generatedMonths
  }, [months, monthFormatter])

  // Calendar boundary helpers
  const { windowStart, currentMonthDate } = useMemo(() => {
    const now = new Date()
    const sorted = [...monthsToRender].sort(
      (prev, next) => prev.date.getTime() - next.date.getTime()
    )
    return {
      windowStart: sorted[0]?.date ?? now,
      currentMonthDate: new Date(now.getFullYear(), now.getMonth(), 1)
    }
  }, [monthsToRender])

  const isMonthDisabled = useCallback(
    (month: Date) => {
      const id = getMonthId(month)
      if (disabledMap.has(id)) {return disabledMap.get(id)!}
      // Future month check
      if (month > currentMonthDate) {return 'future'}
      // Out of span check
      if (month < windowStart) {return 'outOfSpan'}
      return undefined
    },
    [disabledMap, currentMonthDate, windowStart]
  )

  const createSingleMonthSelection = useCallback(
    (selectedMonthDate: Date) => {
      const endDateForMonth = lastDayOfMonth(selectedMonthDate)
      setStartDate(selectedMonthDate)
      setEndDate(endDateForMonth)
      onChange?.({ start: selectedMonthDate, end: endDateForMonth })
    },
    [onChange]
  )

  const validateRange = useCallback(
    (startDate: Date, endDate: Date) => {
      const checkCursor = new Date(startDate)
      checkCursor.setMonth(checkCursor.getMonth() + 1)
      while (checkCursor < endDate) {
        if (isMonthDisabled(checkCursor)) {
          return false
        }
        checkCursor.setMonth(checkCursor.getMonth() + 1)
      }
      return true
    },
    [isMonthDisabled]
  )

  const createRange = useCallback(
    (startDate: Date, endDate: Date) => {
      let newStartDate = startDate
      let newEndDate = endDate
      // ensure chronological order
      if (endDate < startDate) {
        newStartDate = endDate
        newEndDate = startDate
      }

      if (!validateRange(newStartDate, newEndDate)) {
        return false
      }

      setStartDate(newStartDate)
      setEndDate(lastDayOfMonth(newEndDate))
      onChange?.({ start: newStartDate, end: lastDayOfMonth(newEndDate) })
      return true
    },
    [validateRange, onChange]
  )

  const handleMonthClick = useCallback(
    (selectedMonthDate: Date) => {
      const disabledReason = isMonthDisabled(selectedMonthDate)

      if (disabledReason) {return}

      if (!startDate) {
        createSingleMonthSelection(selectedMonthDate)
        return
      }

      if (startDate && endDate && isSameMonth(startDate, endDate)) {
        if (!createRange(startDate, selectedMonthDate)) {
          createSingleMonthSelection(selectedMonthDate)
        }
        return
      }

      if (startDate && endDate && !isSameMonth(startDate, endDate)) {
        createSingleMonthSelection(selectedMonthDate)
        return
      }

      if (!createRange(startDate, selectedMonthDate)) {
        createSingleMonthSelection(selectedMonthDate)
      }
    },
    [startDate, endDate, createSingleMonthSelection, createRange, isMonthDisabled]
  )

  const isSelected = (monthDate: Date) => {
    return Boolean(
      (startDate && isSameMonth(monthDate, startDate)) ||
        (endDate && isSameMonth(monthDate, endDate))
    )
  }
  const isStartMonth = (monthDate: Date) => Boolean(startDate && isSameMonth(monthDate, startDate))
  const isEndMonth = (monthDate: Date) => Boolean(endDate && isSameMonth(monthDate, endDate))
  const isInRangeMiddle = (monthDate: Date) => {
    if (startDate && endDate) {
      return monthDate > startDate && monthDate < endDate
    }
    return false
  }

  // Helper to get the label for a given date from monthsToRender
  const getLabelForDate = useCallback(
    (date: Date | null | undefined) => {
      if (!date) {return ''}
      const match = monthsToRender.find(opt => isSameMonth(opt.date, date))
      return match ? match.label : monthFormatter.format(date)
    },
    [monthsToRender, monthFormatter]
  )

  const selectedHeaderText = useMemo(() => {
    if (!startDate) {return ''}
    const startLabel = `${getLabelForDate(startDate)} ${startDate.getFullYear()}`
    if (!endDate || isSameMonth(startDate, endDate)) {
      return startLabel
    }
    const endLabel = `${getLabelForDate(endDate)} ${endDate.getFullYear()}`
    return `${startLabel} - ${endLabel}`
  }, [startDate, endDate, getLabelForDate])

  const showPlaceholderHeader = useMemo(() => {
    return !selectedHeaderText && placeholderHeader
  }, [selectedHeaderText, placeholderHeader])

  const showSelectedHeader = useMemo(() => {
    return selectedHeaderText && !showPlaceholderHeader
  }, [selectedHeaderText, showPlaceholderHeader])

  const sortedMonths = useMemo(() => {
    return monthsToRender.sort((prev, next) => prev.date.getTime() - next.date.getTime())
  }, [monthsToRender])

  return (
    <div className={cx('month-range-picker', className)} style={{ maxWidth: '385px' }}>
      {/* Selected range header */}
      {showPlaceholderHeader && (
        <div className="month-range-picker-header-placeholder">{placeholderHeader}</div>
      )}
      {showSelectedHeader && (
        <div className="month-range-picker-header-selected">{selectedHeaderText}</div>
      )}
      <div className="month-range-picker-grid">
        {sortedMonths.map(calendarMonth => {
          const disabledReason = isMonthDisabled(calendarMonth.date)
          return (
            <MonthButton
              key={calendarMonth.date.toISOString()}
              month={calendarMonth}
              isDisabled={!!disabledReason}
              tooltipMessage={disabledReason ? tooltips?.[disabledReason] : undefined}
              isSelected={isSelected(calendarMonth.date)}
              isStartDate={isStartMonth(calendarMonth.date)}
              isEndDate={isEndMonth(calendarMonth.date)}
              isInRange={isInRangeMiddle(calendarMonth.date)}
              onSelect={() => handleMonthClick(calendarMonth.date)}
            />
          )
        })}
      </div>
    </div>
  )
}
